import { DeesElement, customElement, html, css, state, type TemplateResult } from '@design.estate/dees-element';
import { renderIcon } from '../brand.js';
import { base } from '../theme.js';

const SOURCE_URL = 'https://code.foss.global/modelgrid.com';
const LEGAL_URL = 'https://legal.task.vc';
const PRIVACY_URL = 'https://legal.task.vc/privacy';
const INSTALL_COMMAND = 'curl -fsSL https://install.modelgrid.com | bash';

const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The landing page of modelgrid chat at modelgrid.com. */
@customElement('mg-landing')
export class MgLanding extends DeesElement {
  @state() accessor copied = false;

  public static styles = [
    base,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--bg);
        font-size: 16px;
        line-height: 1.5;
      }
      .wrap {
        max-width: 1360px;
        margin: 0 auto;
        padding-left: 40px;
        padding-right: 40px;
      }
      .skip {
        position: absolute;
        left: 16px;
        top: -60px;
        z-index: 10;
        padding: 10px 14px;
        border-radius: 8px;
        background: var(--ink);
        color: var(--bg);
        font-weight: 600;
        text-decoration: none;
      }
      .skip:focus {
        top: 12px;
      }
      code.inline {
        font-size: 14px;
      }

      /* header */
      header .wrap {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 16px 32px;
        padding-top: 22px;
        padding-bottom: 22px;
      }
      .brand {
        display: flex;
        align-items: center;
        gap: 12px;
        text-decoration: none;
      }
      .brand .icon {
        display: block;
        flex: none;
      }
      .brand span {
        font-size: 19px;
        font-weight: 600;
        letter-spacing: -0.02em;
      }
      .brand:hover {
        color: var(--ink);
      }
      nav {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px 28px;
        font-size: 15px;
      }
      nav a {
        color: var(--ink-2);
        text-decoration: none;
      }
      nav a.source {
        display: inline-flex;
        align-items: center;
        min-height: 40px;
        padding: 0 16px;
        border: 1px solid var(--line-strong);
        border-radius: 8px;
        color: var(--ink);
      }

      /* hero */
      .hero {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 32px 64px;
        padding-top: 56px;
        padding-bottom: 40px;
      }
      h1 {
        flex: 999 1 640px;
        min-width: 0;
        margin: 0;
        font-size: 80px;
        line-height: 0.98;
        font-weight: 600;
        letter-spacing: -0.045em;
      }
      .side {
        flex: 1 1 360px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .side p {
        margin: 0;
        color: var(--ink-2);
      }
      .install {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 6px 6px 6px 14px;
        border: 1px solid var(--line-strong);
        border-radius: 10px;
        font-family: var(--mono);
        font-size: 14px;
      }
      .install code {
        flex: 1;
        min-width: 0;
        overflow-x: auto;
        white-space: nowrap;
        scrollbar-width: none;
      }
      .install button {
        flex: none;
        min-height: 36px;
        padding: 0 14px;
        border: 0;
        border-radius: 7px;
        background: var(--ink);
        color: var(--bg);
        font: 600 14px/1 var(--sans);
        cursor: pointer;
      }
      .install button:hover {
        background: #ffffff;
      }
      .go {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 18px;
      }
      .cta {
        display: inline-flex;
        align-items: center;
        min-height: 44px;
        padding: 0 18px;
        border-radius: 8px;
        background: var(--accent);
        color: var(--bg);
        font-weight: 600;
        text-decoration: none;
      }
      .cta:hover {
        color: var(--bg);
        background: #f5b453;
      }
      .once {
        font-family: var(--mono);
        font-size: 13px;
        color: var(--muted);
      }
      .once code {
        color: var(--ink);
      }
      .demo {
        padding-bottom: 72px;
      }
      /* the demo's frame holds its place while its chunk loads */
      mg-chat-demo:not(:defined) {
        display: block;
        height: 640px;
        border: 1px solid var(--line);
        border-radius: 16px;
        background: var(--panel);
      }

      /* sections */
      section.band {
        border-top: 1px solid var(--line);
        scroll-margin-top: 0;
      }
      section.band > .wrap {
        padding-top: 72px;
        padding-bottom: 72px;
      }
      h2 {
        margin: 0;
        font-size: 36px;
        line-height: 1.1;
        font-weight: 600;
        letter-spacing: -0.03em;
      }
      h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 40px;
      }
      .item {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .item p,
      .lede p,
      .card p {
        margin: 0;
        color: var(--ink-2);
      }
      .split {
        display: flex;
        flex-wrap: wrap;
        gap: 32px 64px;
      }
      .split > .lede {
        flex: 1 1 300px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .split > .wide {
        flex: 999 1 560px;
        min-width: 0;
      }
      .cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 24px;
      }
      .card {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 22px;
        border: 1px solid var(--line);
        border-radius: 12px;
      }
      .card .tool {
        font-family: var(--mono);
        color: var(--accent);
      }
      pre {
        margin: 0;
        padding: 22px 24px;
        border: 1px solid var(--line);
        border-radius: 12px;
        background: var(--panel);
        overflow-x: auto;
        font-size: 14px;
        line-height: 1.7;
        color: #d9dad4;
      }
      pre .c {
        color: var(--muted);
      }

      /* footer */
      footer {
        border-top: 1px solid var(--line);
      }
      footer .wrap {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 12px 32px;
        padding-top: 32px;
        padding-bottom: 32px;
        font-size: 14px;
        color: var(--muted);
      }
      footer nav {
        gap: 24px;
        font-size: 14px;
      }
      footer nav a {
        color: var(--muted);
        text-decoration: underline;
      }

      @media (max-width: 760px) {
        .wrap {
          padding-left: 20px;
          padding-right: 20px;
        }
        h1 {
          font-size: 44px;
        }
        mg-chat-demo:not(:defined) {
          height: 590px;
        }
      }
    `,
  ];

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    // the conversation renders modelgrid chat's own elements, the bulk of the code: a chunk of its own,
    // so the page above it does not wait for them
    void import('./mg-chat-demo.js');
  }

  public async firstUpdated(): Promise<void> {
    // fragment links cannot see into the shadow root; this element scrolls to its own sections
    this.shadowRoot?.addEventListener('click', (eventArg) => {
      const link = (eventArg.target as HTMLElement | null)?.closest?.('a[href^="#"]');
      if (!link) return;
      const id = link.getAttribute('href')!.slice(1);
      if (this.scrollToSection(id)) {
        eventArg.preventDefault();
        history.pushState(null, '', id === 'top' ? location.pathname : `#${id}`);
      }
    });
    window.addEventListener('popstate', () => this.scrollToSection(location.hash.slice(1) || 'top', false));
    if (location.hash) {
      await this.updateComplete;
      requestAnimationFrame(() => this.scrollToSection(location.hash.slice(1), false));
    }
  }

  private scrollToSection(idArg: string, smoothArg = true): boolean {
    const target = this.shadowRoot?.getElementById(idArg);
    if (!target) return false;
    const behavior = smoothArg && !prefersReducedMotion() ? 'smooth' : 'auto';
    if (idArg === 'top') window.scrollTo({ top: 0, behavior });
    else target.scrollIntoView({ behavior, block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    return true;
  }

  private async copyInstall(): Promise<void> {
    try {
      await navigator.clipboard.writeText(INSTALL_COMMAND);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1600);
    } catch {
      // without clipboard access the command stays there to select
    }
  }

  public render(): TemplateResult {
    return html`
      <a class="skip" href="#top">Skip to content</a>
      <header>
        <div class="wrap">
          <a class="brand" href="#top">${renderIcon(32, '#ededea', '#08090a')}<span>modelgrid</span></a>
          <nav aria-label="Main">
            <a href="#features">Features</a>
            <a href="#tools">Tools</a>
            <a href="#models">Models</a>
            <a href="#run">Run it</a>
            <a class="source" href=${SOURCE_URL}>Source</a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section class="wrap hero" aria-labelledby="hero-title">
          <h1 id="hero-title">A chat for<br />your models.</h1>
          <div class="side">
            <p>
              Streaming answers with their reasoning, steering while it works, a code sandbox and web reads you approve. On
              your machine, against any OpenAI-compatible server.
            </p>
            <div class="install">
              <code>${INSTALL_COMMAND}</code>
              <button type="button" @click=${this.copyInstall} aria-label=${this.copied ? 'Copied' : 'Copy the install command'}>
                ${this.copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div class="go">
              <a class="cta" href="#run">Get started</a>
              <span class="once">or run it once: <code>npx modelgrid</code></span>
            </div>
          </div>
        </section>

        <section class="wrap demo" aria-label="A conversation in modelgrid, playing as an illustration">
          <mg-chat-demo></mg-chat-demo>
        </section>

        <section id="features" class="band" aria-labelledby="features-title">
          <div class="wrap grid">
            <h2 id="features-title">What it does</h2>
            <div class="item">
              <h3>Steer, stop, undo</h3>
              <p>
                A message sent while the model works steers the running answer. Stop ends it;
                <code class="inline">/undo</code> and <code class="inline">/redo</code> take turns back and forth.
              </p>
            </div>
            <div class="item">
              <h3>Answers with their reasoning</h3>
              <p>
                Streamed as they are written, with each model's reasoning efforts and context window. Long conversations
                compact on their own.
              </p>
            </div>
            <div class="item">
              <h3>Conversations that stay</h3>
              <p>Kept in your data directory and named after their first answer. Images and text files attach to any message.</p>
            </div>
          </div>
        </section>

        <section id="tools" class="band" aria-labelledby="tools-title">
          <div class="wrap split">
            <h2 id="tools-title" class="lede">Tools with limits you can read</h2>
            <div class="wide cards">
              <div class="card">
                <span class="tool">run_code</span>
                <p>
                  The model writes a short TypeScript program that runs in a QuickJS sandbox: calculations, data and text
                  processing. No network, files or timers.
                </p>
              </div>
              <div class="card">
                <span class="tool">web.fetchPage</span>
                <p>
                  Reads a public page as text. You approve each new site; private and local addresses are refused, every
                  redirect checked. 60 s and 2 MiB at most.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="models" class="band" aria-labelledby="models-title">
          <div class="wrap split">
            <div class="lede">
              <h2 id="models-title">Any OpenAI-compatible server</h2>
              <p>vLLM, ollama, OpenAI or the AGL inference proxy: one model list across all of them. Built on FlexHarness.</p>
            </div>
            <pre class="wide"><span class="c"># point it at your own servers</span>
MODELGRID_BACKENDS='[{"name": "local",
  "baseUrl": "http://localhost:8000/v1"}]' npx modelgrid

<span class="c">modelgrid 0.1.0 on http://localhost:3140</span>
<span class="c">  open: http://localhost:3140/#token=…</span></pre>
          </div>
        </section>

        <section id="run" class="band" aria-labelledby="run-title">
          <div class="wrap grid">
            <h2 id="run-title">Yours, on your machine</h2>
            <div class="item">
              <h3>Local first</h3>
              <p>
                It listens on 127.0.0.1 and every request carries an access token only you can read. Sign-in for teams comes
                with idp.global.
              </p>
            </div>
            <div class="item">
              <h3>In your workspace</h3>
              <p>
                Opens as an app in a workspace.global desktop: follows its theme, attaches files from its drives and saves
                conversations to them.
              </p>
            </div>
            <div class="item">
              <h3>Open source</h3>
              <p>MIT licensed, on code.foss.global.</p>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div class="wrap">
          <span>© Task Venture Capital GmbH</span>
          <nav aria-label="Legal">
            <a href=${LEGAL_URL}>Imprint</a>
            <a href=${PRIVACY_URL}>Privacy</a>
            <a href=${SOURCE_URL}>Source</a>
          </nav>
        </div>
      </footer>
    `;
  }
}
